import { GlobalSettings } from "../global_settings.js";
import { CrashBreadcrumbs } from "./breadcrumbs.js";
import { CrashCheckpoints } from "./checkpoints.js";
import { CrashConfig } from "./crash_config.js";
import { CrashEventBuilder } from "./event_builder.js";
import { CrashIdentity } from "./identity.js";
import { NativeBridge } from "./native_bridge.js";
import { VersionLoader } from "../version_loader.js";
import { CrashStateSnapshot, type SnapshotProvider } from "./state_snapshot.js";
import { CrashTransport } from "./transport.js";
import { runWhenIdle } from "../utils/idle_task.js";
import type { CheckpointName, CrashEvent, CrashKind, CrashMechanism } from "./types.js";

let installed = false;
let sent_this_session = 0;
let dropped_this_session = 0;
let last_key = '';
let last_key_at = 0;
let reporting_console = false;
const uncaught_errors = new WeakSet<object>();
const console_keys_this_session = new Set<string>();
let snapshot_timer: ReturnType<typeof setInterval> | null = null;

export function crashCapture( error: unknown, extra?: Record<string, unknown>, handled = true ): void
{
  try
  {
    void CrashReporter.report( error, { handled, mechanism: 'manual', extra } );
  }
  catch ( e )
  {
    console.error( '[Crash] capture failed', e, error, extra );
  }
}

export class CrashReporter
{
  public static install(): void
  {
    if ( installed ) return;
    installed = true;

    try { ( Error as unknown as { stackTraceLimit?: number } ).stackTraceLimit = 50; } catch {}
    CrashIdentity.init();
    CrashCheckpoints.init();
    CrashStateSnapshot.init();

    window.addEventListener( 'error', event =>
    {
      const error = event.error ?? new Error( typeof event.message === 'string' ? event.message : 'Unhandled error' );
      if ( error && typeof error === 'object' ) uncaught_errors.add( error );
      attachLocationStack( error, event );
      void this.report( error, {
        handled: false,
        mechanism: 'onerror',
        extra: { source: event.filename, lineno: event.lineno, colno: event.colno },
      } );
    }, { capture: true } );

    window.addEventListener( 'unhandledrejection', event =>
    {
      void this.report( event.reason ?? new Error( 'Unhandled rejection' ), { handled: false, mechanism: 'unhandledrejection' } );
    } );

    hookConsole();

    if ( !NativeBridge.isAvailable() )
    {
      window.addEventListener( 'pagehide', () => CrashCheckpoints.mark( 'clean_exit' ) );
      window.addEventListener( 'online', () => { void CrashTransport.flush(); } );
    }

    CrashCheckpoints.mark( 'bootstrap_js' );
    snapshot_timer = setInterval( () => this.tick(), CrashConfig.SNAPSHOT_INTERVAL_MS );
    const count_frame = (): void =>
    {
      CrashStateSnapshot.countFrame();
      window.requestAnimationFrame( count_frame );
    };
    window.requestAnimationFrame( count_frame );

    void this.configureNative().then( () => this.reportPreviousRun() );
    exposeDebug();
  }

  public static setSnapshotProvider( provider: SnapshotProvider ): void
  {
    CrashStateSnapshot.setProvider( provider );
  }

  public static setScreenProvider( provider: () => string | null ): void
  {
    CrashEventBuilder.setScreenProvider( provider );
  }

  public static checkpoint( name: CheckpointName ): void
  {
    CrashCheckpoints.mark( name );
  }

  public static breadcrumb( type: 'screen' | 'level' | 'dialog' | 'app' | 'ws', data: string ): void
  {
    CrashBreadcrumbs.add( type, data );
  }

  public static async report( error: unknown, options: { handled: boolean; mechanism: CrashMechanism; extra?: Record<string, unknown> | null; kind?: CrashKind } ): Promise<void>
  {
    const key = dedupeKey( error );
    const now = Date.now();
    if ( key === last_key && now - last_key_at < CrashConfig.DEDUPE_WINDOW_MS )
    {
      dropped_this_session++;
      return;
    }
    last_key = key;
    last_key_at = now;

    if ( sent_this_session >= CrashConfig.MAX_EVENTS_PER_SESSION )
    {
      dropped_this_session++;
      return;
    }
    sent_this_session++;

    const event = CrashEventBuilder.build( {
      kind: options.kind ?? 'js',
      handled: options.handled,
      mechanism: options.mechanism,
      error,
      extra: options.extra ?? null,
      dropped: dropped_this_session,
    } );
    dropped_this_session = 0;
    await CrashTransport.send( event );
  }

  public static async flush(): Promise<{ sent: number; pending: number }>
  {
    return CrashTransport.flush();
  }

  private static tick_pending = false;

  private static tick(): void
  {
    if ( this.tick_pending ) return;
    this.tick_pending = true;
    runWhenIdle( () =>
    {
      this.tick_pending = false;
      const snapshot = CrashStateSnapshot.tick();
      if ( snapshot?.screen === 'game' ) CrashCheckpoints.mark( 'in_game' );
    }, CrashConfig.SNAPSHOT_INTERVAL_MS );
  }

  private static async configureNative(): Promise<void>
  {
    const config = await NativeBridge.configure( CrashConfig.buildId(), GlobalSettings.ENV );
    if ( config )
    {
      CrashEventBuilder.setAppInfo( config.version, config.version_code, config.native_build_id );
      return;
    }
    try
    {
      const version = await VersionLoader.load();
      CrashEventBuilder.setAppInfo( version, VersionLoader.getBuildNumber() || null, null );
    }
    catch {}
  }

  private static async reportPreviousRun(): Promise<void>
  {
    const previous = CrashCheckpoints.consumePreviousRun();
    if ( !previous || !CrashCheckpoints.previousRunWasAbnormal() ) return;

    const native = await NativeBridge.getPendingExit();
    if ( native?.handled )
    {
      console.info( `[Crash] previous run ended at ${previous.name}, native reported: ${native.reason ?? 'crash'}` );
      return;
    }

    const event: CrashEvent = CrashEventBuilder.build( {
      kind: 'abnormal_exit',
      handled: false,
      mechanism: 'checkpoint',
      checkpoint: previous,
      snapshot: CrashStateSnapshot.previousRun(),
      screen: readString( CrashStateSnapshot.previousRun()?.screen ),
      tags: { last_checkpoint: previous.name, exit_reason: native?.reason ?? 'unknown' },
      extra: {
        previous_session_id: previous.session_id,
        since_checkpoint_ms: Math.max( 0, Date.now() - Date.parse( previous.at ) ),
      },
    } );
    event.identity = { ...event.identity, session_id: previous.session_id };
    event.exception = {
      type: 'AbnormalExit',
      message: `Previous run ended after checkpoint ${previous.name}`,
      stack: null,
      cause: null,
    };
    console.info( `[Crash] previous run ended abnormally at ${previous.name}` );
    await CrashTransport.send( event );
  }
}

function readString( value: unknown ): string | null
{
  return typeof value === 'string' ? value : null;
}

function dedupeKey( error: unknown ): string
{
  if ( error instanceof Error )
  {
    const stack = typeof error.stack === 'string' ? error.stack.split( '\n' ).slice( 0, 4 ).join( '|' ) : '';
    return `${error.name}:${error.message}:${stack}`;
  }
  return String( error );
}

function hookConsole(): void
{
  for ( const level of [ 'error', 'warn' ] as const )
  {
    const original = console[level].bind( console );
    console[level] = ( ...args: unknown[] ): void =>
    {
      try { CrashBreadcrumbs.add( 'console', `${level}: ${args.map( formatArg ).join( ' ' )}` ); }
      catch {}
      original( ...args );
      if ( level === 'error' ) reportConsoleError( args );
    };
  }
}

function reportConsoleError( args: unknown[] ): void
{
  if ( reporting_console ) return;
  const error = args.find( a => a instanceof Error );
  if ( !error ) return;
  setTimeout( () => reportConsoleErrorDeferred( error, args ), 0 );
}

function reportConsoleErrorDeferred( error: Error, args: unknown[] ): void
{
  if ( reporting_console || uncaught_errors.has( error ) ) return;
  const key = dedupeKey( error );
  if ( console_keys_this_session.has( key ) ) return;
  if ( console_keys_this_session.size >= CrashConfig.MAX_CONSOLE_ERRORS_PER_SESSION ) return;
  console_keys_this_session.add( key );
  const text = args.filter( a => a !== error ).map( formatArg ).join( ' ' ).slice( 0, 300 );
  reporting_console = true;
  try
  {
    void CrashReporter.report( error, { handled: true, mechanism: 'console', kind: 'error', extra: text ? { console: text } : null } )
      .catch( () => {} )
      .finally( () => { reporting_console = false; } );
  }
  catch
  {
    reporting_console = false;
  }
}

function formatArg( value: unknown ): string
{
  if ( value instanceof Error ) return `${value.name}: ${value.message}`;
  if ( typeof value === 'string' ) return value;
  try { return JSON.stringify( value ) ?? String( value ); }
  catch { return String( value ); }
}

function exposeDebug(): void
{
  ( window as any ).crashDebug = {
    js: () => { setTimeout( () => { throw new Error( 'Crash test: JS error (async)' ); }, 0 ); },
    rejection: () => { void Promise.reject( new Error( 'Crash test: unhandled rejection' ) ); },
    captured: () => crashCapture( new Error( 'Crash test: captured' ), { phase: 'debug' } ),
    java: () => NativeBridge.debugCrash( 'java' ),
    native: () => NativeBridge.debugCrash( 'native' ),
    anr: () => NativeBridge.debugCrash( 'anr' ),
    renderer: () => NativeBridge.debugCrash( 'renderer' ),
    oom: () => NativeBridge.debugCrash( 'oom' ),
    flush: () => CrashTransport.flush(),
    pending: async () => NativeBridge.isAvailable() ? NativeBridge.pending() : { pending: CrashTransport.pendingCount(), files: [] },
    state: () => ( {
      checkpoint: CrashCheckpoints.current_checkpoint(),
      previous: CrashCheckpoints.previousRun(),
      snapshot: CrashStateSnapshot.current(),
      identity: CrashIdentity.get(),
      breadcrumbs: CrashBreadcrumbs.list(),
      build_id: CrashConfig.buildId(),
      sent: sent_this_session,
      dropped: dropped_this_session,
    } ),
    stop: () => { if ( snapshot_timer ) clearInterval( snapshot_timer ); },
  };
}

function attachLocationStack( error: unknown, event: ErrorEvent ): void
{
  if ( !error || typeof error !== 'object' ) return;
  const target = error as { name?: unknown; message?: unknown; stack?: unknown };
  if ( typeof target.stack === 'string' || typeof event.filename !== 'string' || !event.filename ) return;
  const name = typeof target.name === 'string' && target.name ? target.name : 'Error';
  const message = typeof target.message === 'string' ? target.message : '';
  const stack = `${name}: ${message}\n    at <anonymous> (${event.filename}:${event.lineno}:${event.colno})`;
  try { Object.defineProperty( target, 'stack', { value: stack, configurable: true, writable: true } ); }
  catch {}
}
